// La lista de la oficina: la bandeja de entrada (Entrega 3, 04-11; maqueta web/qa/auditoria-apple/informe/maquetas/
// lista-despues.png). Bloques: Te toca · Han terminado · En marcha y, plegados en una línea, Con Santi › · Aparcados › ·
// Se van ›. Una decisión = una fila (la subida de un agente va en su fila; las sueltas, arriba). La lógica (bloques,
// orden, frases) vive en situacion.ts y se calcula una vez en useEquipo; aquí solo se pinta. Escritorio y móvil.
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, DoorOpen, Handshake, ListFilter, Moon, MoreHorizontal, PanelLeftClose, Plus, Search, ShieldCheck, X, type LucideIcon } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { cerrarConDeshacer, useCierresStore } from '../cierre/cerrarConDeshacer';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { LineaCuentasMovil } from '../cuentas/CuentasClaude';
import { BandejaDespliegues } from '../despliegues/BandejaDespliegues';
import { subidasSueltas } from '../despliegues/subidas';
import { useSubidas } from '../despliegues/store';
import { hiloAlAbrir, usePelotasStore } from '../pelotas/store';
import { useEsperasDeGrupos, type EsperaDeGrupo } from '../pelotas/porAgente';
import { useToastStore } from '../../../stores/toast.store';
import { useOficinaStore, type FiltroGrupo } from '../store';
import { Boton } from '../ui';
import { IsotipoVenuz } from '../ui/IsotipoVenuz';
import type { PestanaBandeja } from './bandejas';
import { hace, SUPERFICIE } from './estado';
import { FilaEquipo } from './FilaEquipo';
import { recordarFocoAlAbrir } from './focoAlCerrar';
import { enFiltro, opcionesVer } from './filtroGrupo';
import { asasOrdenQuieto, fotoDe } from './ordenQuieto';
import { abrirSaltar } from './Saltar';
import { aparcadoDe, grupoDelContratado, PLEGADOS, tituloBloque, type Bloque } from './situacion';
import { useEquipo } from './useEquipo';

export function ListaEquipo({
  contratados,
  abierto,
  alAbrir,
  alContratar,
  alResaltar,
  alPlegar,
  conCuentas = false,
}: {
  contratados: Record<string, ContratadoDTO>;
  abierto: string | null;
  alAbrir: (agentId: string) => void;
  alContratar: () => void;
  alResaltar?: (agentId: string | null) => void;
  /** Móvil (22-10): la línea de las cuentas de Claude junto al resumen; en escritorio va el cartel de la pared. */
  conCuentas?: boolean;
  /** S13 — plegar la lista a una columna de caras (solo en la oficina de escritorio/iPad; en móvil no hay). */
  alPlegar?: () => void;
}) {
  // 06-10 — una bandeja por persona: cada uno ve sus agentes (los que contrató) y, con el selector, la de otro.
  const { mia, pestanas, clave, sabido, vista, visibles: todos, grupos: bloquesTodos, santi, subidas, ahora } = useEquipo(contratados);
  const verBandeja = useOficinaStore((s) => s.verBandeja);
  // 04-11 — el filtro «Ver» (mesas de equipo): va ENCIMA de los bloques de useEquipo (mismo orden, mismos bloques).
  const filtro = useOficinaStore((s) => s.filtroGrupo);
  const setFiltro = useOficinaStore((s) => s.setFiltroGrupo);
  const gruposMesa = useOficinaStore((s) => s.grupos);
  const salas = useOficinaStore((s) => s.salas);
  const esperas = useEsperasDeGrupos();
  const grupos = useMemo(
    () => (filtro ? bloquesTodos.map((b) => ({ ...b, miembros: b.miembros.filter((c) => enFiltro(c, filtro)) })).filter((b) => b.miembros.length > 0) : bloquesTodos),
    [bloquesTodos, filtro],
  );
  // Lo urgente que el filtro deja fuera (grupos.md §5): nunca se esconde.
  const fuera = useMemo(() => (filtro ? (bloquesTodos.find((b) => b.bloque === 'te_toca')?.miembros ?? []).filter((c) => !enFiltro(c, filtro)) : []), [bloquesTodos, filtro]);
  const ver = useMemo(() => opcionesVer(salas, gruposMesa, todos, (id) => !!esperas.get(id)?.conSanti), [salas, gruposMesa, todos, esperas]);
  // Caso 1: el grupo filtrado se queda vacío → vuelve a «Todos» y lo dice.
  const filtroVacio = !!filtro?.grupoId && !Object.values(contratados).some((c) => c.grupoId === filtro.grupoId);
  useEffect(() => {
    if (!filtroVacio || !filtro) return;
    setFiltro(null);
    useToastStore.getState().push({ message: t('oficinaV2.grupos.filtroVacio', { nombre: filtro.nombre }), durationMs: 4_000 });
  }, [filtroVacio, filtro, setFiltro]);
  // Si se abre un agente que el filtro deja fuera (Alt+↓ desde su chat, Ctrl+K, la oficina), el filtro se quita: la lista
  // enseña siempre al abierto.
  useEffect(() => {
    const c = abierto ? contratados[abierto] : undefined;
    if (filtro && c && !enFiltro(c, filtro)) setFiltro(null);
    // Solo al cambiar de abierto: con él abierto Pau puede volver a filtrar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);
  const saltarFuera = () => {
    const c = fuera[0];
    if (!c) return;
    setFiltro(null);
    alAbrir(c.agentId);
  };
  // Alt+↓ (también con la lista filtrada y sin chat: «N te esperan fuera · Alt ↓») es de toda la oficina: useSiguiente.ts.
  // La que se miraba se quedó sin agentes: se vuelve a la propia (y no salta sola si ese dueño contrata otro).
  useEffect(() => {
    if (vista && vista !== clave) verBandeja(null);
  }, [vista, clave, verBandeja]);
  const ajena = pestanas.find((p) => p.clave === clave && !p.propia);

  // Producción es una sola: las subidas que no van en la fila de un agente de esta bandeja salen sueltas, arriba.
  const { pendientes } = useSubidas();
  const sueltas = useMemo(() => {
    const enFilas = new Set(todos.flatMap((c) => (subidas.get(c.agentId) ? [subidas.get(c.agentId)!.runId] : [])));
    return subidasSueltas(pendientes, enFilas);
  }, [pendientes, subidas, todos]);

  const cuenta = (b: Bloque) => grupos.find((g) => g.bloque === b)?.miembros.length ?? 0;
  const teEsperan = cuenta('te_toca');
  const enMarcha = cuenta('en_marcha');

  // Los plegados se abren al pulsar su línea (o desde el resumen). Se recuerda solo mientras se mira.
  const [abiertos, setAbiertos] = useState<ReadonlySet<Bloque>>(new Set());
  const alternar = (b: Bloque, abrir?: boolean) =>
    setAbiertos((s) => {
      const n = new Set(s);
      if (abrir ?? !n.has(b)) n.add(b);
      else n.delete(b);
      return n;
    });
  const bloques = useRef<Partial<Record<Bloque, HTMLElement | null>>>({});
  // flujo.md #8 — cada trozo del resumen lleva a su bloque (y lo abre si está plegado).
  const irA = (b: Bloque) => {
    if (PLEGADOS.includes(b)) alternar(b, true);
    const el = bloques.current[b];
    el?.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const lineaSanti = useLineaSanti();
  // Al cerrar el chat, el foco vuelve a la fila desde la que se abrió (focoAlCerrar.ts).
  const abrirFila = (id: string) => {
    recordarFocoAlAbrir();
    alAbrir(id);
  };
  // 05-11 (QA G3): con el puntero (o el foco del teclado) sobre las filas, el orden no se mueve (ordenQuieto.ts). La foto
  // es la de los bloques que se ven (sin filtro: el filtro va encima).
  const ultimos = useRef({ clave, bloquesTodos });
  ultimos.current = { clave, bloquesTodos };
  const asas = useMemo(() => asasOrdenQuieto(() => fotoDe(ultimos.current.clave, ultimos.current.bloquesTodos)), []);
  // El grupo de cada fila, con el mismo contexto que sus bloques (useEquipo): la misma clasificación en todas partes.
  const fila = (c: ContratadoDTO) => (
    <FilaEquipo
      key={c.agentId}
      c={c}
      grupo={grupoDelContratado(c, santi.get(c.agentId), { subida: subidas.get(c.agentId) ?? null, ahora })}
      santi={santi.get(c.agentId)}
      subida={subidas.get(c.agentId)}
      ahora={ahora}
      activo={c.agentId === abierto}
      alAbrir={abrirFila}
      alResaltar={alResaltar}
    />
  );
  const miembros = (b: Bloque) => grupos.find((g) => g.bloque === b)?.miembros ?? [];

  return (
    <nav aria-label={t('oficinaV2.equipo.titulo')} className={cn('flex max-h-full flex-col overflow-hidden rounded-o-l', SUPERFICIE)}>
      <header className="px-4 pb-2 pt-3.5">
        <div className="flex items-center gap-3">
          {alPlegar ? (
            <button
              type="button"
              onClick={alPlegar}
              aria-label={t('oficinaV2.equipo.plegar')}
              title={t('oficinaV2.equipo.plegar')}
              className="-my-1 -ml-2 -mr-2 inline-flex h-11 w-11 items-center justify-center rounded-o-s text-oficina-texto2 transition-colors hover:bg-oficina-hover hover:text-oficina-texto focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
              data-testid="plegar-lista"
            >
              <PanelLeftClose className="h-[18px] w-[18px]" aria-hidden />
            </button>
          ) : null}
          <h1 className="flex flex-1 items-center gap-2 text-o-titulo font-bold text-oficina-texto [@media(pointer:coarse)]:text-o-titulo2"><IsotipoVenuz className="h-5 w-5 shrink-0" />{t('oficinaV2.titulo')}</h1>
          {/* 09-10 (Pau aprobó «Cuentas a un menú»): lo que se usa de vez en cuando va en «⋯», no a la vista. */}
          <Menu.Root>
            <Menu.Trigger asChild>
              <button
                type="button"
                aria-label={t('oficinaV2.equipo.masOpciones')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-o-s text-oficina-texto2 transition-colors hover:bg-oficina-hover hover:text-oficina-texto focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70 data-[state=open]:bg-oficina-hover data-[state=open]:text-oficina-texto"
                data-testid="menu-equipo"
              >
                <MoreHorizontal className="h-[18px] w-[18px]" aria-hidden />
              </button>
            </Menu.Trigger>
            <Menu.Portal>
              <Menu.Content align="end" sideOffset={6} className="z-[70] min-w-[220px] rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
                <Menu.Item asChild>
                  <a
                    href="/cuentas"
                    className="flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]"
                    data-testid="ir-a-cuentas"
                  >
                    <ShieldCheck className="h-4 w-4 text-oficina-texto2" aria-hidden />
                    {t('oficinaV2.equipo.cuentasYAccesos')}
                  </a>
                </Menu.Item>
              </Menu.Content>
            </Menu.Portal>
          </Menu.Root>
          {ver.length ? <BotonVer ver={ver} filtro={filtro} total={todos.length} alElegir={setFiltro} /> : null}
          <Boton variante="primario" tamano="sm" icono={Plus} onClick={alContratar} aria-label={ver.length ? t('oficinaV2.contratarCorto') : undefined}>
            {/* Con «Ver» al lado, en el iPad (zonas de 44 px) no caben las cuatro piezas: «Contratar» se queda en su «+». */}
            {ver.length ? <span className="[@media(pointer:coarse)]:sr-only">{t('oficinaV2.contratarCorto')}</span> : t('oficinaV2.contratarCorto')}
          </Boton>
        </div>
        {/* 03-11 (rebrand): el buscador de Spotlight (Ctrl/⌘+K) también a la vista, como el campo de búsqueda de Mensajes. */}
        <button
          type="button"
          onClick={abrirSaltar}
          className="mt-2.5 flex h-9 w-full items-center gap-2 rounded-o-m bg-oficina-control px-3 text-left text-o-texto text-oficina-texto3 outline-none transition-colors hover:bg-oficina-pulsado focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          data-testid="abrir-saltar"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{t('oficinaV2.saltar.placeholder')}</span>
          <kbd className="rounded-o-s border border-oficina-linea px-1.5 font-sans text-o-mini text-oficina-texto3 [@media(pointer:coarse)]:hidden">{t('oficinaV2.saltar.atajo')}</kbd>
        </button>
        {sabido && pestanas.length > 1 ? <SelectorBandeja pestanas={pestanas} clave={clave} alElegir={(c) => verBandeja(c === mia ? null : c)} /> : null}
        {ajena ? (
          <p className="mt-2 text-o-sub leading-5 text-oficina-texto2" data-testid="bandeja-ajena">
            {t('oficinaV2.s12.bandejas.mirando', { nombre: ajena.nombre })} ·{' '}
            <button type="button" onClick={() => verBandeja(null)} className="font-semibold text-oficina-enlace underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70">
              {t('oficinaV2.s12.bandejas.volver')}
            </button>
          </p>
        ) : null}
        {filtro ? <FiltroPuesto filtro={filtro} fuera={fuera.length} alQuitar={() => setFiltro(null)} alSaltar={saltarFuera} /> : null}
        {todos.length && !filtro ? (
          // flujo.md #8 — el resumen lleva a algún sitio: cada trozo es un botón que baja a su bloque (maqueta: «4 te esperan
          // · 5 en marcha»; «Han terminado» se ve justo debajo y no cabe en una línea de 336 px). Lo que no necesita a Pau
          // (un problema que arregla la oficina) ya no va aquí. A la derecha, el atajo del bucle.
          <div className="mt-2 flex items-center gap-3" data-testid="resumen-lista">
            <p className="min-w-0 flex-1 text-o-sub leading-5 text-oficina-texto2">
              {[
                teEsperan ? (
                  <Trozo key="te_toca" fuerte alPulsar={() => irA('te_toca')} bloque="te_toca" testid="te-esperan">
                    {t(teEsperan === 1 ? 'oficinaV2.equipo.teEsperaUno' : 'oficinaV2.equipo.teEsperan', { n: teEsperan })}
                  </Trozo>
                ) : (
                  <span key="nada" className="font-semibold text-oficina-texto">{t('oficinaV2.s12.resumen.nadaTeToca')}</span>
                ),
                enMarcha ? (
                  <Trozo key="en_marcha" alPulsar={() => irA('en_marcha')} bloque="en_marcha">
                    {t('oficinaV2.lista.resumen.enMarcha', { n: enMarcha })}
                  </Trozo>
                ) : null,
              ]
                .filter(Boolean)
                .map((x, i) => (
                  <Fragment key={i}>
                    {i ? ' · ' : null}
                    {x}
                  </Fragment>
                ))}
            </p>
            {teEsperan ? (
              // Con el dedo no hay teclado: solo la cuenta.
              <span className="shrink-0 text-o-sub text-oficina-texto3 [@media(pointer:coarse)]:hidden" data-testid="pista-siguiente">
                {t('oficinaV2.lista.resumen.siguiente')} <kbd className="ml-1 rounded-o-s border border-oficina-linea px-1.5 font-sans text-o-mini">{t('oficinaV2.lista.resumen.atajo')}</kbd>
              </span>
            ) : null}
            {conCuentas ? <LineaCuentasMovil /> : null}
          </div>
        ) : null}
      </header>

      <div className="min-h-0 overflow-y-auto px-2 py-2 [scrollbar-width:thin]" {...asas} data-testid="filas-equipo">
        {/* Producción es una sola: las subidas sin agente en esta bandeja salen en todas, arriba. */}
        <BandejaDespliegues sueltas={sueltas} />
        {grupos
          .filter((g) => !PLEGADOS.includes(g.bloque))
          .map(({ bloque, miembros: gente }) => (
            <section key={bloque} ref={(el) => void (bloques.current[bloque] = el)} className="mb-1.5 scroll-mt-2" data-testid={`bloque-${bloque}`}>
              <h2 className="flex min-h-8 items-center gap-1.5 px-2 pb-1 pt-2 text-o-sub font-semibold text-oficina-texto2">
                <span className="flex-1">{tituloBloque(bloque)}</span>
                {bloque === 'han_terminado' && gente.length > 1 ? <CerrarLosN miembros={gente} /> : <span className="text-o-nota font-medium tabular-nums text-oficina-texto3">{gente.length}</span>}
              </h2>
              <ul className="space-y-0.5">{gente.map(fila)}</ul>
            </section>
          ))}
        {/* Plegados al fondo, una línea cada uno (orden.md §3 regla 11). «Con Santi» está siempre que haya tablero:
            es la puerta a lo de Santi (su widget dejó la franja de arriba). */}
        {lineaSanti || cuenta('con_santi') ? (
          <LineaPlegada
            bloque="con_santi"
            icono={Handshake}
            colorIcono="text-oficina-santi"
            n={cuenta('con_santi')}
            abierto={abiertos.has('con_santi')}
            alAlternar={() => alternar('con_santi')}
            refBloque={(el) => void (bloques.current.con_santi = el)}
            extra={lineaSanti}
          >
            <ConSantiPorGrupos miembros={miembros('con_santi')} santi={santi} esperas={esperas} fila={fila} />
          </LineaPlegada>
        ) : null}
        {cuenta('aparcados') ? (
          <LineaPlegada
            bloque="aparcados"
            icono={Moon}
            n={cuenta('aparcados')}
            abierto={abiertos.has('aparcados')}
            alAlternar={() => alternar('aparcados')}
            refBloque={(el) => void (bloques.current.aparcados = el)}
            pista={(() => {
              const k = miembros('aparcados').filter((c) => aparcadoDe(c, santi.get(c.agentId), { ahora })?.proponerCerrar).length;
              return k ? t('oficinaV2.lista.aparcado.paraCerrar', { n: k }) : undefined;
            })()}
          >
            {miembros('aparcados').map(fila)}
          </LineaPlegada>
        ) : null}
        {cuenta('se_van') ? (
          // 29-10 (Pau): los que se van, al fondo y plegados: una línea apagada que se abre si quiere mirarlos.
          <LineaPlegada bloque="se_van" icono={DoorOpen} n={cuenta('se_van')} abierto={abiertos.has('se_van')} alAlternar={() => alternar('se_van')} refBloque={(el) => void (bloques.current.se_van = el)}>
            {miembros('se_van').map(fila)}
          </LineaPlegada>
        ) : null}
        {!todos.length ? (
          <div className="flex flex-col items-center px-6 pb-6 pt-4 text-center">
            <h2 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.equipo.vacioTitulo')}</h2>
            <p className="mt-1.5 text-o-cuerpo leading-relaxed text-oficina-texto2">{t('oficinaV2.equipo.vacio')}</p>
          </div>
        ) : null}
      </div>
    </nav>
  );
}

/** El botón «Ver» (filtro, como en Mail): Todos · por sala, sus grupos y su «Sin grupo», con cuántos. */
function BotonVer({ ver, filtro, total, alElegir }: { ver: ReturnType<typeof opcionesVer>; filtro: FiltroGrupo | null; total: number; alElegir: (f: FiltroGrupo | null) => void }) {
  const fila = 'flex min-h-9 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-1.5 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]';
  const marca = (si: boolean) => (si ? <Check className="h-4 w-4 shrink-0 text-oficina-texto" aria-hidden /> : <span className="h-4 w-4 shrink-0" aria-hidden />);
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button
          type="button"
          aria-label={t('oficinaV2.grupos.ver')}
          title={t('oficinaV2.grupos.ver')}
          className={cn(
            'inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
            filtro ? 'bg-oficina-texto text-oficina-fondo' : 'text-oficina-texto2 hover:bg-oficina-hover hover:text-oficina-texto data-[state=open]:bg-oficina-hover data-[state=open]:text-oficina-texto',
          )}
          data-testid="boton-ver"
        >
          <ListFilter className="h-[18px] w-[18px]" aria-hidden />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="end" sideOffset={6} collisionPadding={8} className="z-[70] max-h-[70vh] min-w-64 overflow-y-auto rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95" data-testid="menu-ver">
          <Menu.Label className="px-3 pb-1 pt-1.5 text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.grupos.ver')}</Menu.Label>
          <Menu.Item onSelect={() => alElegir(null)} className={fila}>
            {marca(!filtro)}
            <span className="flex-1">{t('oficinaV2.grupos.todos')}</span>
            <span className="text-o-sub tabular-nums text-oficina-texto3">{total}</span>
          </Menu.Item>
          {ver.map(({ sala, opciones }) => (
            <Menu.Group key={sala.projectId}>
              <Menu.Separator className="my-1 h-px bg-oficina-linea" />
              <Menu.Label className="px-3 pb-1 pt-1.5 text-o-nota font-semibold text-oficina-texto3">{sala.nombre}</Menu.Label>
              {opciones.map((o) => (
                <Menu.Item
                  key={o.grupoId ?? 'sin'}
                  onSelect={() => alElegir({ projectId: sala.projectId, grupoId: o.grupoId, nombre: o.grupoId ? o.nombre : t('oficinaV2.grupos.sinGrupo') })}
                  className={fila}
                >
                  {marca(filtro?.projectId === sala.projectId && filtro.grupoId === o.grupoId)}
                  <span className="min-w-0 flex-1 truncate">{o.grupoId ? o.nombre : t('oficinaV2.grupos.sinGrupo')}</span>
                  {o.conSanti ? <Handshake className="h-3.5 w-3.5 shrink-0 text-oficina-santi" aria-label={t('oficinaV2.grupos.esperandoSanti')} /> : null}
                  <span className="text-o-sub tabular-nums text-oficina-texto3">{o.n}</span>
                </Menu.Item>
              ))}
            </Menu.Group>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** Con el filtro puesto: la etiqueta «Precios ✕» y, si algo urgente queda fuera, «N te espera(n) fuera · Alt ↓». */
function FiltroPuesto({ filtro, fuera, alQuitar, alSaltar }: { filtro: FiltroGrupo; fuera: number; alQuitar: () => void; alSaltar: () => void }) {
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="filtro-puesto">
      <button
        type="button"
        onClick={alQuitar}
        aria-label={t('oficinaV2.grupos.quitarFiltro', { nombre: filtro.nombre })}
        className="inline-flex h-8 items-center gap-1.5 rounded-full bg-oficina-texto pl-3 pr-2 text-o-sub font-semibold text-oficina-fondo outline-none transition-colors hover:bg-oficina-texto2 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
        data-testid="filtro-etiqueta"
      >
        {filtro.nombre}
        <X className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden />
      </button>
      {fuera ? (
        // Lo que deja pasar un modo Concentración: es para actuar, así que lleva el oro (apagado: no es el botón principal).
        <button
          type="button"
          onClick={alSaltar}
          className="inline-flex h-8 items-center gap-2 rounded-full bg-oficina-acento/15 pl-3 pr-1.5 text-o-sub font-semibold text-oficina-acento outline-none transition-colors hover:bg-oficina-acento/25 focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
          data-testid="te-espera-fuera"
        >
          {t(fuera === 1 ? 'oficinaV2.grupos.fueraUno' : 'oficinaV2.grupos.fuera', { n: fuera })}
          <kbd className="rounded-o-s border border-oficina-acento/40 px-1.5 font-sans text-o-mini [@media(pointer:coarse)]:hidden">{t('oficinaV2.lista.resumen.atajo')}</kbd>
        </button>
      ) : null}
    </div>
  );
}

/**
 * Dentro de «Con Santi» (grupos.md §5): primero cada grupo que espera un contrato, con su cabecera («🤝 Esperando a Santi
 * · contrato «Precios del CRM» · la pelota la tiene Santi desde hace 2 h») y debajo sus agentes; después los que tienen
 * un contrato propio. El orden es el de useEquipo (situacion.ts ya los pone juntos).
 */
function ConSantiPorGrupos({
  miembros,
  santi,
  esperas,
  fila,
}: {
  miembros: ContratadoDTO[];
  santi: ReadonlyMap<string, { grupo?: string }>;
  esperas: ReadonlyMap<string, EsperaDeGrupo>;
  fila: (c: ContratadoDTO) => React.ReactNode;
}) {
  const gruposMesa = useOficinaStore((s) => s.grupos);
  const piezas: React.ReactNode[] = [];
  let anterior: string | null = null;
  for (const c of miembros) {
    const grupo = santi.get(c.agentId)?.grupo ?? null;
    const espera = grupo ? esperas.get(grupo) : undefined;
    if (grupo && grupo !== anterior && espera) {
      const nombre = gruposMesa.find((g) => g.id === grupo)?.nombre ?? '';
      piezas.push(
        <li key={`cabecera-${grupo}`} className="mx-1 mb-1 mt-1.5 rounded-o-m bg-oficina-santi/10 px-3 py-2" data-testid="cabecera-espera">
          <p className="truncate text-o-sub text-oficina-texto2">
            <Handshake className="mr-1.5 inline h-3.5 w-3.5 align-[-2px] text-oficina-santi" strokeWidth={2.2} aria-hidden />
            <span className="font-semibold text-oficina-santi">{nombre}</span> · {t('oficinaV2.grupos.contratoDe', { titulo: espera.contrato.titulo })}
          </p>
          <p className="mt-0.5 truncate text-o-nota text-oficina-texto3">{t('oficinaV2.grupos.pelotaDesde', { cuando: hace(espera.contrato.desde) })}</p>
        </li>,
      );
    }
    anterior = grupo;
    piezas.push(fila(c));
  }
  return <>{piezas}</>;
}

/** Un trozo del resumen: un botón con aspecto de texto que lleva a su bloque. */
function Trozo({ fuerte = false, bloque, alPulsar, testid, children }: { fuerte?: boolean; bloque: Bloque; alPulsar: () => void; testid?: string; children: string }) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      aria-label={`${children}. ${t('oficinaV2.lista.resumen.irA', { bloque: tituloBloque(bloque) })}`}
      className={cn('rounded-o-s underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-oficina-enlace/70', fuerte ? 'font-semibold text-oficina-texto' : 'text-oficina-texto2')}
      data-testid={testid ?? `resumen-${bloque}`}
    >
      {children}
    </button>
  );
}

/** «Cerrar los N» en el título de «Han terminado»: los cierra todos, con un solo «Deshacer». */
function CerrarLosN({ miembros }: { miembros: ContratadoDTO[] }) {
  const enMarcha = useCierresStore((s) => miembros.every((c) => s.fases[c.agentId]));
  return (
    <Boton
      tamano="sm"
      cargando={enMarcha}
      onClick={() => cerrarConDeshacer(miembros)}
      aria-label={t('oficinaV2.lista.accion.cerrarLosAria', { n: miembros.length })}
      className="-my-1 h-7 min-h-0 px-3 text-o-nota"
      data-testid="cerrar-los-n"
    >
      {t('oficinaV2.lista.accion.cerrarLos', { n: miembros.length })}
    </Boton>
  );
}

/**
 * Lo que la línea «Con Santi» dice de su tablero: cuántas cosas nuevas tiene y abrirlo (pelotas/store `abrirSanti`, la
 * ventana de siempre). null si la oficina no está conectada a su tablero.
 */
function useLineaSanti(): { nuevos: number; abrir: () => void } | null {
  const lista = usePelotasStore((s) => s.lista);
  const abrirSanti = usePelotasStore((s) => s.abrirSanti);
  if (!lista?.configurado) return null;
  return { nuevos: lista.pared.nuevos, abrir: () => abrirSanti(hiloAlAbrir(lista)) };
}

/**
 * Una línea plegada al fondo (Con Santi, Aparcados, Se van): icono, título, cuántos y la flecha. Pulsarla abre su gente
 * debajo. «Con Santi» lleva además, a la derecha, «N nuevos ›» que abre lo de Santi; sin gente con Santi, la línea
 * entera lo abre.
 */
function LineaPlegada({
  bloque,
  icono: Icono,
  colorIcono = 'text-oficina-texto2',
  n,
  abierto,
  alAlternar,
  refBloque,
  pista,
  extra,
  children,
}: {
  bloque: Bloque;
  icono: LucideIcon;
  colorIcono?: string;
  n: number;
  abierto: boolean;
  alAlternar: () => void;
  refBloque: (el: HTMLElement | null) => void;
  pista?: string;
  extra?: { nuevos: number; abrir: () => void } | null;
  children?: React.ReactNode;
}) {
  const soloSanti = !!extra && n === 0;
  const nuevos = extra?.nuevos ? t(extra.nuevos === 1 ? 'oficinaV2.lista.santi.nuevo' : 'oficinaV2.lista.santi.nuevos', { n: extra.nuevos }) : null;
  const fila = 'flex min-h-11 items-center gap-3 rounded-o-m px-2.5 text-left outline-none transition-colors hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-oficina-enlace/70';
  return (
    <section ref={refBloque} className="mb-0.5 scroll-mt-2" data-testid={`linea-${bloque}`}>
      <div className="flex items-center">
        <button
          type="button"
          onClick={soloSanti ? extra!.abrir : alAlternar}
          aria-expanded={soloSanti ? undefined : abierto}
          aria-label={soloSanti ? `${tituloBloque(bloque)}${nuevos ? `, ${nuevos}` : ''}. ${t('oficinaV2.lista.santi.abrir')}` : undefined}
          className={cn(fila, 'min-w-0 flex-1')}
          data-testid={`plegado-${bloque}`}
        >
          <Icono className={cn('h-[18px] w-[18px] shrink-0', colorIcono)} strokeWidth={2} aria-hidden />
          <span className="min-w-0 flex-1 truncate text-o-cuerpo text-oficina-texto">{tituloBloque(bloque)}</span>
          {pista ? <span className="shrink-0 text-o-nota font-semibold text-oficina-bloqueado" data-testid={`pista-${bloque}`}>{pista}</span> : null}
          {soloSanti ? (
            nuevos ? <span className="shrink-0 text-o-sub text-oficina-texto2">{nuevos}</span> : null
          ) : (
            <span className="shrink-0 text-o-sub tabular-nums text-oficina-texto3">{n}</span>
          )}
          <ChevronRight className={cn('h-4 w-4 shrink-0 text-oficina-texto3 transition-transform duration-150 ease-o-apple motion-reduce:transition-none', abierto && !soloSanti && 'rotate-90')} aria-hidden />
        </button>
        {extra && !soloSanti ? (
          <button type="button" onClick={extra.abrir} className={cn(fila, 'shrink-0 gap-1 text-o-sub text-oficina-texto2')} aria-label={t('oficinaV2.lista.santi.abrir')} data-testid="abrir-santi">
            {nuevos ?? t('oficinaV2.lista.santi.abrir')}
            <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
          </button>
        ) : null}
      </div>
      {abierto && !soloSanti ? <ul className="mt-0.5 space-y-0.5">{children}</ul> : null}
    </section>
  );
}

/** Las bandejas: la propia («Mía») y las de los demás que tienen agentes, con cuántos esperan a su dueño. */
function SelectorBandeja({ pestanas, clave, alElegir }: { pestanas: PestanaBandeja[]; clave: string; alElegir: (clave: string) => void }) {
  return (
    <div role="group" aria-label={t('oficinaV2.s12.bandejas.selector')} className="mt-2 flex flex-wrap gap-1.5" data-testid="selector-bandeja">
      {pestanas.map((p) => {
        const activa = p.clave === clave;
        return (
          <button
            key={p.clave}
            type="button"
            aria-pressed={activa}
            onClick={() => alElegir(p.clave)}
            className={cn(
              'inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-o-sub transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70',
              activa ? 'bg-oficina-texto font-semibold text-oficina-fondo' : 'bg-white/[0.06] text-oficina-texto2 hover:bg-white/[0.1] hover:text-oficina-texto',
            )}
          >
            {p.propia ? t('oficinaV2.s12.bandejas.mia') : p.nombre}
            {!p.propia && p.teToca ? (
              <>
                <span aria-hidden className={cn('rounded-full px-1.5 text-o-mini font-semibold', activa ? 'bg-oficina-fondo/20' : 'bg-oficina-bloqueado/20 text-oficina-bloqueado')}>
                  {p.teToca}
                </span>
                <span className="sr-only">{t('oficinaV2.s12.bandejas.esperan', { n: p.teToca, nombre: p.nombre })}</span>
              </>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
